/**
 * uscitaProtetta.js — «vuoi uscire senza salvare», una volta sola per
 * tutto il gestionale.
 *
 * Due strumenti, da usare insieme:
 *
 *   const sporco = useSporco({ campoA, campoB }, versione);
 *   useUscitaProtetta(sporco);
 *
 * `useSporco` dice se il modulo e' diverso da com'era: confronta il
 * valore di ADESSO con l'istantanea presa l'ultima volta che `versione`
 * e' cambiata. Non e' un flag «toccato»: chi scrive una lettera e la
 * cancella non ha modificato niente, e un flag direbbe di si'. La
 * schermata alza `versione` dopo il caricamento e dopo ogni salvataggio
 * riuscito — nello stesso giro di setState dei dati, cosi' l'istantanea
 * e' quella dei dati appena arrivati e non di quelli di prima.
 *
 * `useUscitaProtetta` dichiara il modulo sporco; a fermare chi sta per
 * perdere il lavoro e' `GuardiaUscita`, montata UNA volta sulla rotta
 * radice, nei tre modi in cui lo si perde: una voce di menu o un
 * collegamento (useBlocker, che sa di ogni navigazione del router), il
 * tasto indietro del browser (idem: ascolta anche popstate) e la
 * chiusura o il ricaricamento della scheda (beforeunload: il browser
 * mostra il suo avviso, il testo non si sceglie). Nei primi due casi la
 * domanda e' MESSAGGIO_USCITA, e i manuali la citano alla lettera.
 *
 * useBlocker funziona solo col router «con dati»: e' il motivo per cui
 * il 21 settembre 2026 App.js e' passato a createBrowserRouter.
 *
 * Il tablet in sala d'attesa (pages/Tablet.jsx) resta FUORI, di
 * proposito: un dialogo «vuoi uscire senza salvare» davanti a un
 * paziente e' un dialogo che il paziente non sa interpretare. Li' non
 * c'e' menu ne' tasto indietro utile; il rischio e' il ricaricamento o
 * la sessione che scade, e la protezione e' lo stato della sessione di
 * firma, che la segreteria vede dalla schermata «Tablet».
 *
 * Il cambio di scheda dentro una pagina (?vista=...) fatto con un clic NON
 * passa di qui: chi lo fa chiede prima con confermaUscita() (la scheda del
 * paziente). Dal 28 settembre 2026 le linguette del paziente stanno
 * nell'indirizzo, perche' «Indietro» ci torni (Alex): quindi col tasto
 * INDIETRO (navigazione POP) anche un cambio di sola query chiede, come un
 * cambio di percorso.
 */
import React, { useEffect, useRef } from 'react';
import { useBlocker, Outlet } from 'react-router-dom';
import Notifiche from '../components/Notifiche';
import { installaUscitaSicura, segnaUscitaSenzaSalvare } from './uscitaSicura';
import { useVersioneInLinea } from './versioneInLinea';

export const MESSAGGIO_USCITA =
    'Ci sono modifiche non salvate. Vuoi uscire senza salvare?';

/* Dove si sa QUANTI campi non sono salvati - oggi il riquadro fisiologico,
   che salva campo per campo e li conta nella sua barra - la domanda lo
   dice (22 settembre 2026). E' l'ultimo momento prima di perdere del
   testo, e il numero c'e' gia'. Dove non si sa, resta MESSAGGIO_USCITA. */
export const messaggioCampiNonSalvati = (n) => (n === 1
    ? "C'e' un campo non salvato. Vuoi uscire senza salvarlo?"
    : `Ci sono ${n} campi non salvati. Vuoi uscire senza salvarli?`);

const serializza = (v) => JSON.stringify(v === undefined ? null : v);

export function useSporco(valore, versione) {
    const attuale = serializza(valore);
    const base = useRef({ versione, istantanea: attuale });
    if (base.current.versione !== versione) {
        base.current = { versione, istantanea: attuale };
    }
    return attuale !== base.current.istantanea;
}

/* Quanti moduli sporchi sono montati adesso. E' UN contatore per tutta
   l'applicazione, e c'e' un motivo preciso: il router ammette UN solo
   blocker alla volta - con due useBlocker montati consulta solo l'ultimo
   e avvisa in console. Quindi i moduli non bloccano: si contano qui, e a
   bloccare e' GuardiaUscita, montata una volta sola in App.js, che legge
   il contatore al momento della navigazione.

   Serve anche alle pagine che cambiano schermata SENZA passare dal
   router - la scheda del paziente, che tiene le linguette in uno stato
   locale: li' si chiede con confermaUscita() prima di smontare. E' un
   contatore e non un booleano perche' due moduli sporchi nella stessa
   pagina sono un caso possibile (una scheda e un modulo dentro), e il
   primo che si pulisce non deve liberare l'altro. */
const sporchi = new Map();   // id del modulo -> { conto, salvaPrima, nonMandato }

/* QUANTO SI ASPETTA IL SALVATAGGIO PRIMA DI CHIEDERE (Alex, 14/10/2026:
   uscendo con un campo scritto si SALVA prima, e la domanda «modifiche non
   salvate» compare solo se il salvataggio fallisce o non finisce in tempo,
   mai quando va a buon fine). Tre secondi: le scritture di questi moduli
   sono una riga sola (decine di millisecondi sul banco), quindi oltre i tre
   secondi la rete o il server sono in difficolta' - e un clic su una voce di
   menu che non risponde per di piu' sembra rotto. Passati i tre secondi si fa
   la domanda di sempre, e a decidere e' chi lavora. E' un oggetto e non una
   costante perche' la prova lo accorcia. */
export const ATTESA_SALVATAGGIO = { ms: 3000 };

/* Fa salvare ai moduli sporchi quello che hanno in mano, e dice se e'
   andato tutto. Un modulo che non si salva da solo (una riga nuova, un
   modulo con il suo pulsante) non e' salvabile da qui: la risposta e' no, e
   si fa la domanda. */
async function salvaPrimaDiUscire() {
    const moduli = [...sporchi.values()];
    if (moduli.length === 0) return true;
    if (moduli.some((m) => !m.salvaPrima)) return false;
    let timer;
    const scaduto = new Promise((risolvi) => { timer = setTimeout(() => risolvi(false), ATTESA_SALVATAGGIO.ms); });
    const tutti = Promise.all(moduli.map((m) => Promise.resolve().then(() => m.salvaPrima()).catch(() => false)))
        .then((esiti) => esiti.every(Boolean));
    try { return await Promise.race([tutti, scaduto]); } finally { clearTimeout(timer); }
}

/** Come confermaUscita, ma prima SALVA: per chi cambia schermata o linguetta
    senza passare dal router (la scheda del paziente). Risolve true se si puo'
    uscire. */
export async function confermaUscitaSalvando() {
    if (sporchi.size === 0) return true;
    if (await salvaPrimaDiUscire()) return true;
    const si = window.confirm(messaggioUscita());
    if (si) segnaUscitaSenzaSalvare();
    return si;
}
let prossimoId = 0;

/* Il numero si scrive solo se TUTTI i moduli sporchi lo sanno: un modulo
   che non conta e uno che dice «2» insieme non fanno «2 campi». */
function messaggioUscita() {
    const conti = [...sporchi.values()].map((m) => m.conto);
    if (conti.length && conti.every((n) => Number.isInteger(n) && n > 0)) {
        return messaggioCampiNonSalvati(conti.reduce((a, b) => a + b, 0));
    }
    return MESSAGGIO_USCITA;
}

export const confermaUscita = () =>
    sporchi.size === 0 || window.confirm(messaggioUscita());

/** C'e' almeno un modulo sporco montato? Senza domande: lo chiede la
    ricarica automatica (utils/versioneInLinea.js), che con un modulo
    sporco non ricarica. */
export const ciSonoModuliSporchi = () => sporchi.size > 0;

/**
 * Il modulo dichiara se e' sporco; ritorna chiedi(), per le finestre che
 * si chiudono da sole - clic sullo sfondo, la X, «Annulla» - dove il
 * router non c'entra: la finestra chiede e chiude solo se la risposta
 * e' si'.
 */
export function useUscitaProtetta(sporco, campiNonSalvati = null, uscita = null) {
    const id = useRef(null);
    /* Come uscire senza perdere niente (Alex, 14/10/2026), dal modulo che si
       salva da solo (useSalvaCampo): `salvaPrima` salva e risolve true se e'
       andato tutto, `nonMandato` dice se c'e' qualcosa di scritto che non e'
       stato nemmeno mandato (un salvataggio fallito, un valore che non puo'
       partire). Letti al momento, dall'ultimo render. */
    const uscitaOra = useRef(null);
    uscitaOra.current = uscita;
    const conSalva = !!(uscita && uscita.salvaPrima);
    if (id.current === null) { prossimoId += 1; id.current = prossimoId; }
    const conto = Number.isInteger(campiNonSalvati) && campiNonSalvati > 0 ? campiNonSalvati : null;

    useEffect(() => {
        if (!sporco) return undefined;
        const chiave = id.current;
        sporchi.set(chiave, {
            conto,
            salvaPrima: conSalva ? () => uscitaOra.current.salvaPrima() : null,
            /* Senza una risposta si presume di si': e' il comportamento di prima. */
            nonMandato: () => !(uscitaOra.current && uscitaOra.current.nonMandato) || uscitaOra.current.nonMandato(),
        });
        return () => { sporchi.delete(chiave); };
    }, [sporco, conto, conSalva]);

    return () => !sporco
        || window.confirm(conto ? messaggioCampiNonSalvati(conto) : MESSAGGIO_USCITA);
}

/**
 * L'unico blocker dell'applicazione: sta sulla rotta radice di App.js e
 * rende le rotte figlie. Ferma le navigazioni del router (menu,
 * collegamenti, tasto indietro) quando c'e' almeno un modulo sporco
 * montato, e la chiusura o il ricaricamento della scheda con
 * beforeunload (li' l'avviso lo mostra il browser, con le sue parole).
 * Il cambio di sola query (?tab=...) non e' un'uscita.
 */
export function GuardiaUscita() {
    const blocco = useBlocker(({ currentLocation, nextLocation, historyAction }) =>
        sporchi.size > 0 && (currentLocation.pathname !== nextLocation.pathname
            || (historyAction === 'POP' && currentLocation.search !== nextLocation.search)));

    const incorso = useRef(false);
    useEffect(() => {
        if (blocco.state !== 'blocked' || incorso.current) return;
        incorso.current = true;
        /* PRIMA SI SALVA (Alex, 14/10/2026): si aspetta il salvataggio dei
           moduli che si salvano da soli, al massimo ATTESA_SALVATAGGIO; se e'
           andato tutto si passa senza domande. Solo se fallisce, o non finisce
           in tempo, o c'e' un modulo che non si salva da solo, si fa la
           domanda di sempre. window.confirm e' sincrono e modale: nessun altro
           clic passa finche' non si e' risposto, che e' esattamente quello che
           serve qui. Un dialogo disegnato a mano lascerebbe la pagina viva
           sotto. */
        confermaUscitaSalvando().then((si) => {
            incorso.current = false;
            if (si) blocco.proceed();
            else blocco.reset();
        });
    }, [blocco]);

    /* Il campo che si stava scrivendo non si perde se la pagina se ne va:
       vedi utils/uscitaSicura.js. Si installa una volta, qui, sulla radice. */
    useEffect(() => { installaUscitaSicura(); }, []);

    /* La terza difesa (Alex, 2/10/2026): una versione nuova in linea, e la
       pagina si ricarica da sola quando nessuno sta scrivendo. Anche lei una
       volta sola, qui: vedi utils/versioneInLinea.js. */
    useVersioneInLinea(ciSonoModuliSporchi);

    useEffect(() => {
        const avvisa = (e) => {
            /* L'avviso del browser solo se c'e' qualcosa di scritto e NON
               ancora mandato (Alex, 14/10/2026): un campo gia' partito - anche
               se la risposta non e' ancora tornata - viaggia con keepalive e
               non lo fa scattare. Il campo scritto e non uscito lo segnala
               uscitaSicura.js. */
            if (![...sporchi.values()].some((m) => m.nonMandato())) return;
            e.preventDefault();
            /* returnValue e' quello che i browser guardano ancora; il
               testo lo ignorano tutti da anni. */
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', avvisa);
        return () => window.removeEventListener('beforeunload', avvisa);
    }, []);

    /* Le notifiche stanno qui, sulla radice, per la stessa ragione del
       blocker: una volta sola, e anche fuori dal Layout (tablet, login). */
    return React.createElement(React.Fragment, null,
        React.createElement(Notifiche),
        React.createElement(Outlet));
}
